import { useTranslation } from 'next-i18next'
import { Fragment } from 'react'
import { useTheme } from 'styled-components'

import {
  getRealEstateListingAttributes,
  getRealEstateTitleAttributes,
} from '@/components/srp/search-list/getRealEstateListingAttributes'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { getAttributesCanonicalValuesDictionary } from '@/features/attributes/utils/getAttributesCanonicalValuesDictionary'
import { type GenericListing } from '@/features/listing/types/listings'
import { useLocale } from '@/hooks/useLocale'
import { BodyText } from '@/ui/atoms/body-text/BodyText'
import { Flex } from '@/ui/atoms/flex/Flex'

export const RealEstateListingTitle = ({ listing }: { listing: GenericListing }) => {
  const { t } = useTranslation(TRANSLATION_KEYS.SRP)
  const { apiLocale } = useLocale()
  const { colors, spacing } = useTheme()

  const { categoryId } = listing
  const attributes = 'attributes' in listing ? listing.attributes : undefined
  const nearestIntersection =
    'nearestIntersection' in listing.location && Array.isArray(listing.location.nearestIntersection)
      ? listing.location.nearestIntersection
      : undefined

  const attributesDictionary = getAttributesCanonicalValuesDictionary(attributes?.all)

  const { realEstateAttributes } = getRealEstateListingAttributes(
    attributesDictionary,
    nearestIntersection,
    categoryId,
    t
  )
  const titleLabels = getRealEstateTitleAttributes(realEstateAttributes, apiLocale, t)

  if (titleLabels.length === 0) return null

  return (
    <Flex alignItems="center" gap={spacing.defaultSmall} data-testid="real-estate-title">
      {titleLabels.map((label, index) => {
        const labelKey = `label-${label.replaceAll(' ', '-')}`
        return (
          <Fragment key={labelKey}>
            <span>{label}</span>
            {index < titleLabels.length - 1 && (
              <BodyText
                color={colors.grey.light3}
                as="span"
                aria-hidden="true"
                data-testid="listing-dot-divider"
              >
                •
              </BodyText>
            )}
          </Fragment>
        )
      })}
    </Flex>
  )
}
